/**
 * Admin API: the decoration method catalogue, its base prices per sales
 * platform, the assignments of methods to positions and a product's price
 * overrides. The price table reads the same catalogue; see
 * `product-prices.ts` for its own routes.
 */
import apiClient, { ApiResponse } from '../client';
import type { DecorationMethod } from '../products';

/** Sales platforms decoration prices are maintained for. */
export type SalesPlatform = 'DIRECT_SALE' | 'SELLER_CREATOR';

export const SALES_PLATFORMS: SalesPlatform[] = [
  'DIRECT_SALE',
  'SELLER_CREATOR',
];

/** One method assigned to a position, as returned by the admin detail view. */
export interface PositionDecorationMethodAssignment {
  id: string;
  decorationMethodId: string;
  isDefault: boolean;
  decorationMethod: DecorationMethod;
}

/** Base price of a method on one platform. Decimal string on the wire. */
export interface DecorationMethodPlatformPrice {
  id: string;
  platform: SalesPlatform;
  price: string;
}

export interface AdminDecorationMethod extends DecorationMethod {
  /** Phoenix processing_type id; null for methods without a counterpart. */
  externalId: number | null;
  /** The catalogue lists inactive methods too; the price table hides them. */
  isActive?: boolean;
  platformPrices: DecorationMethodPlatformPrice[];
}

/** A product's price override for one method and platform. */
export interface ProductDecorationMethodPrice {
  id: string;
  decorationMethodId: string;
  platform: SalesPlatform;
  price: string;
}

/**
 * List the decoration method catalog with Phoenix ids and base prices.
 */
export async function getAdminDecorationMethods(): Promise<
  AdminDecorationMethod[]
> {
  const response = await apiClient.get<ApiResponse<AdminDecorationMethod[]>>(
    '/admin/decoration-methods'
  );
  return response.data.data;
}

/**
 * Set or remove the base price of a method on one platform.
 *
 * Pass null to remove the price row, which marks the method unpriced on
 * that platform. 0 is a real price (free decoration).
 */
export async function setDecorationBasePrice(
  methodId: string,
  platform: SalesPlatform,
  price: number | null
): Promise<{ platform: SalesPlatform; price: string | null }> {
  const response = await apiClient.put<
    ApiResponse<{ platform: SalesPlatform; price: string | null }>
  >(`/admin/decoration-methods/${methodId}/prices/${platform}`, { price });
  return response.data.data;
}

/**
 * Assign a decoration method to a position.
 *
 * Assignments are consumed live by the Creator and Direct Sale flows, so
 * the change takes effect immediately.
 */
export async function assignDecorationMethod(
  positionId: string,
  methodId: string
): Promise<PositionDecorationMethodAssignment> {
  const response = await apiClient.post<
    ApiResponse<PositionDecorationMethodAssignment>
  >(`/admin/products/positions/${positionId}/decoration-methods/${methodId}`);
  return response.data.data;
}

/**
 * Remove a decoration method from a position. Runs the same withdrawal as
 * the price table for this one position: designs that relied on it as the
 * position default are pinned to it first, and a new default is picked.
 * Answers 409 (`isLastMethodConflict`) when it is the position's last
 * active method.
 */
export async function unassignDecorationMethod(
  positionId: string,
  methodId: string
): Promise<void> {
  await apiClient.delete(
    `/admin/products/positions/${positionId}/decoration-methods/${methodId}`
  );
}

/**
 * Mark an assigned method as the position default. Designs that relied on
 * the previous default (no method of their own) are pinned to it first, so
 * nothing stored changes its effective method; the previous default is
 * cleared in the same transaction.
 */
export async function setDefaultDecorationMethod(
  positionId: string,
  methodId: string
): Promise<PositionDecorationMethodAssignment> {
  const response = await apiClient.put<
    ApiResponse<PositionDecorationMethodAssignment>
  >(
    `/admin/products/positions/${positionId}/decoration-methods/${methodId}/default`
  );
  return response.data.data;
}

/**
 * Set or reset a product's decoration price override.
 *
 * Pass null to remove the override, which resets the product to the
 * platform base price. 0 is a real override (free decoration) and stays
 * distinct from "no override".
 */
export async function setProductDecorationPrice(
  productId: string,
  methodId: string,
  platform: SalesPlatform,
  price: number | null
): Promise<{ platform: SalesPlatform; price: string | null }> {
  const response = await apiClient.put<
    ApiResponse<{ platform: SalesPlatform; price: string | null }>
  >(
    `/admin/products/${productId}/decoration-method-prices/${methodId}/${platform}`,
    { price }
  );
  return response.data.data;
}
